import React, { useMemo } from 'react'

import { QuestionCircleOutlined } from '@ant-design/icons'
import { Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService } from '@shared/table/TableIndex'

interface OrderSerialNumbersListProps {
  service: TableService;
  role?: string;
}

interface OrderSerialNumberRow {
  id: string;
  serial_number?: string;
  order_id?: string;
  order_name?: string;
  product_id?: string;
  product_name?: string;
  product_sku?: string;
  shipped_at?: string;
  created_at?: string;
  [key: string]: unknown;
}

const OrderSerialNumbersList: React.FC<OrderSerialNumbersListProps> = ({
  service,
  role = 'operator',
}) => {
  const { t } = useTranslation()

  const columns = useMemo(() => [
    {
      nameKey: 'operator.pages.orders.serial_numbers.serial_number',
      name: (
        <Space size={4}>
          {t('operator.pages.orders.serial_numbers.serial_number')}
          <Tooltip title={t('operator.pages.orders.serial_numbers.column_tooltips.serial_number')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      persist: true,
      sortable: true,
      sortFieldName: 'product_serial_numbers.number',
      width: '200px',
      wrap: true,
      selector: ({ serial_number }: OrderSerialNumberRow) => serial_number,
    },
    {
      nameKey: 'operator.pages.orders.serial_numbers.order_name',
      persist: true,
      sortable: true,
      sortFieldName: 'orders.name',
      width: '180px',
      wrap: true,
      cell: ({ order_id, order_name }: OrderSerialNumberRow) => (
        order_id ? (
          <Link to={`/orders/${order_id}`}>
            {order_name || '-'}
          </Link>
        ) : '-'
      ),
    },
    {
      nameKey: 'operator.pages.orders.serial_numbers.product_name',
      persist: true,
      sortable: true,
      sortFieldName: 'products.name',
      width: '250px',
      wrap: true,
      cell: ({ product_id, product_name, product_sku }: OrderSerialNumberRow) => (
        <div>
          <div>
            {product_id ? (
              <Link to={`/products/${product_id}`}>{product_name}</Link>
            ) : (
              product_name
            )}
          </div>
          <div style={{ color: '#999', fontSize: '12px' }}>{product_sku}</div>
        </div>
      ),
    },
    {
      nameKey: 'operator.pages.orders.serial_numbers.shipped_at',
      name: (
        <Space size={4}>
          {t('operator.pages.orders.serial_numbers.shipped_at')}
          <Tooltip title={t('operator.pages.orders.serial_numbers.column_tooltips.shipped_at')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'orders.shipped_at',
      width: '150px',
      wrap: true,
      selector: ({ shipped_at }: OrderSerialNumberRow) => shipped_at || '-',
    },
    {
      nameKey: 'operator.pages.orders.serial_numbers.created_at',
      sortable: true,
      sortFieldName: 'fulfillment_item_serial_numbers.created_at',
      width: '150px',
      wrap: true,
      selector: ({ created_at }: OrderSerialNumberRow) => created_at || '-',
    },
  ], [t])

  const supportRules = useMemo(() => [
    {
      label: t('operator.pages.orders.serial_numbers.serial_number'),
      component: MultipleValue,
      inputName: 'serial_numbers.serial_number',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('operator.pages.orders.serial_numbers.filter_tooltips.serial_number.title'),
        content: t('operator.pages.orders.serial_numbers.filter_tooltips.serial_number.content'),
      },
    },
    {
      label: t('operator.pages.orders.serial_numbers.order_name'),
      component: MultipleValue,
      inputName: 'orders.name',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('operator.pages.orders.serial_numbers.filter_tooltips.order_name.title'),
        content: t('operator.pages.orders.serial_numbers.filter_tooltips.order_name.content'),
      },
    },
    {
      label: t('models.product.sku'),
      component: MultipleValue,
      inputName: 'products.sku',
      createable: true,
      tooltip: {
        title: t('operator.pages.orders.serial_numbers.filter_tooltips.sku.title'),
        content: t('operator.pages.orders.serial_numbers.filter_tooltips.sku.content'),
      },
    },
    {
      label: t('models.product.name'),
      component: MultipleValue,
      inputName: 'products.name',
      createable: true,
      tooltip: {
        title: t('operator.pages.orders.serial_numbers.filter_tooltips.product_name.title'),
        content: t('operator.pages.orders.serial_numbers.filter_tooltips.product_name.content'),
      },
    },
  ], [t])

  return (
    <TableIndex
      columns={columns}
      searchable={{ match: 'contains', searchFields: [t('operator.pages.orders.serial_numbers.serial_number'), t('operator.pages.orders.serial_numbers.order_name')] }}
      columnsEditable={true}
      tableId={`${role}OrderSerialNumbersTable`}
      defaultQuery={{
        sorting_field: 'fulfillment_item_serial_numbers.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      service={service}
    />
  )
}

export default OrderSerialNumbersList
